import React, { useState } from 'react';
import {
  useSetSkyboxCredentials, useSetSkyboxSessionTokens, useClearSkyboxSession, revealCredentials,
} from '../hooks/useSkyboxSessions';

// Per-pricer Skybox login (Admin → Users, super admin only).
//
// A pricer with a live Skybox session gets their own name in Skybox's listing
// history for price changes (instead of "Prime Lion Tickets") and their own
// 15-calls-per-10-s pricing budget. The server cannot log into Skybox itself
// (Akamai challenges it), so the super admin stores the pricer's Skybox login
// here as the record, then runs the assisted Chrome script once to capture the
// session; the server keeps it alive from there.

const STATUS = {
  ok: { label: 'Skybox: live', className: 'tag tag-pricer' },
  dead: { label: 'Skybox: dead', className: 'tag tag-inactive' },
  needsLogin: { label: 'Skybox: needs login', className: 'tag tag-needs-login' },
  none: { label: 'Skybox: none', className: 'tag tag-none' },
};

// A stored login is not a session: the server cannot log in itself, so a pricer
// sits at 'needs login' until the Chrome script captures their token pair.
function statusKey(session) {
  if (session?.status === 'ok') return 'ok';
  if (session?.status === 'dead') return 'dead';
  if (session?.hasPassword) return 'needsLogin';
  return 'none';
}

export function SkyboxSessionChip({ session }) {
  const key = statusKey(session);
  const s = STATUS[key];
  let title;
  if (key === 'ok') title = `${session.email || ''} · ${session.callsLast10s || 0}/15 calls in the last 10 s`;
  else if (key === 'needsLogin') title = `${session.email || 'Login'} stored — run refresh-skybox-token.js for this user to capture the session`;
  else title = session?.lastError || 'No Skybox login stored for this user';
  return <span className={s.className} title={title} style={{ whiteSpace: 'nowrap' }}>{s.label}</span>;
}

export function SkyboxSessionPanel({ userId, username, session, credentialsEnabled, onMessage }) {
  const [email, setEmail] = useState(session?.email || '');
  const [password, setPassword] = useState('');
  const [revealed, setRevealed] = useState(null);
  const [showPaste, setShowPaste] = useState(false);
  const [token, setToken] = useState('');
  const [refreshToken, setRefreshToken] = useState('');
  const setCreds = useSetSkyboxCredentials();
  const setTokens = useSetSkyboxSessionTokens();
  const clear = useClearSkyboxSession();
  const busy = setCreds.isPending || setTokens.isPending || clear.isPending;
  const serverUrl = typeof window !== 'undefined' ? window.location.origin : '';
  const renewCommand = `node backend/scripts/refresh-skybox-token.js --user ${username} --server ${serverUrl}`;

  const fail = (err, fallback) => onMessage?.(err?.response?.data?.error || fallback, true);

  const saveCreds = async () => {
    try {
      await setCreds.mutateAsync({ id: userId, email: email.trim(), password });
      setPassword('');
      onMessage?.('Skybox login saved');
    } catch (err) { fail(err, 'Failed to save the Skybox login'); }
  };
  const reveal = async () => {
    try {
      const res = await revealCredentials(userId);
      setRevealed(res.data);
    } catch (err) { fail(err, 'No stored password to show'); }
  };
  const savePair = async () => {
    try {
      const res = await setTokens.mutateAsync({ id: userId, token: token.trim(), refreshToken: refreshToken.trim() || undefined });
      setToken(''); setRefreshToken(''); setShowPaste(false);
      const s = res.data?.session;
      onMessage?.(s?.status === 'ok' ? `Skybox session is live for ${username}` : `Skybox rejected the session: ${s?.lastError || 'unknown'}`, s?.status !== 'ok');
    } catch (err) { fail(err, 'Failed to store the session'); }
  };
  const forget = async () => {
    if (!window.confirm(`Forget ${username}'s Skybox login and session? Their price changes will show as Prime Lion Tickets again.`)) return;
    try {
      await clear.mutateAsync({ id: userId });
      setEmail(''); setPassword(''); setRevealed(null);
      onMessage?.('Skybox login removed');
    } catch (err) { fail(err, 'Failed to remove the Skybox login'); }
  };

  return (
    <div className="skybox-session-panel">
      <div className="skybox-session-head">
        <strong>Skybox login</strong>
        <SkyboxSessionChip session={session} />
        {session?.expiresAt && session.status === 'ok' && (
          <span className="nr-muted" style={{ fontSize: '0.72rem' }}>refreshes itself · expires {new Date(session.expiresAt).toLocaleTimeString()}</span>
        )}
      </div>
      {session?.status === 'dead' && (
        <div className="message message-error" style={{ marginBottom: 8 }}>
          Session died ({session.lastError || 'unknown'}). Price changes fall back to the API token until it is renewed.
        </div>
      )}
      {!credentialsEnabled && (
        <div className="message message-error" style={{ marginBottom: 8 }}>
          SKYBOX_CRED_KEY is not set on the server, so passwords cannot be stored. Sessions can still be captured.
        </div>
      )}

      <div className="skybox-session-row">
        <input className="form-input" type="email" placeholder="Skybox login email" value={email} onChange={e => setEmail(e.target.value)} autoComplete="off" />
        <input className="form-input" type="password" placeholder={session?.hasPassword ? '•••••••• (stored — type to replace)' : 'Skybox password'} value={password} onChange={e => setPassword(e.target.value)} autoComplete="new-password" />
        <button type="button" className="btn btn-secondary btn-sm" onClick={saveCreds} disabled={busy || !credentialsEnabled || !email.trim() || !password}>Save login</button>
        {session?.hasPassword && (
          revealed
            ? <code className="skybox-session-reveal" title="Shown once; this reveal is logged">{revealed.email} / {revealed.password}</code>
            : <button type="button" className="btn btn-secondary btn-sm" onClick={reveal} disabled={busy}>Show password</button>
        )}
      </div>

      <div className="skybox-session-renew">
        <div className="nr-muted" style={{ fontSize: '0.75rem' }}>
          To start or renew the session, either run the command below on a PC with Chrome and this repo (log in to Skybox as this pricer, open any event's pricing page once; the script sends the session here), or paste the token from Chrome. The server keeps the session alive either way.
        </div>
        <code className="skybox-session-cmd" onClick={() => navigator.clipboard?.writeText(renewCommand)} title="Click to copy">{renewCommand}</code>
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowPaste(v => !v)}>{showPaste ? 'Hide' : 'Paste from Chrome instead'}</button>
        {showPaste && (
          <div className="nr-muted" style={{ fontSize: '0.75rem' }}>
            In Chrome, log in to Skybox as this pricer. Press F12 → Network, open any event's pricing page, click a request to
            skybox.vividseats.com → Headers → Request Headers, and copy <code>x-auth-token</code> into the first box. For a session
            that renews itself, also click the <code>session</code> request → Response and copy <code>refreshToken</code> into the
            second box (log out and in again if it isn't listed).
          </div>
        )}
        {showPaste && (
          <div className="skybox-session-row">
            <input className="form-input" placeholder="X-Auth-Token" value={token} onChange={e => setToken(e.target.value)} autoComplete="off" />
            <input className="form-input" placeholder="refresh token (from the login response)" value={refreshToken} onChange={e => setRefreshToken(e.target.value)} autoComplete="off" />
            <button type="button" className="btn btn-primary btn-sm" onClick={savePair} disabled={busy || token.trim().length < 10}>Save session</button>
          </div>
        )}
      </div>

      {(session?.hasPassword || session?.status !== 'none') && session && (
        <button type="button" className="btn btn-danger btn-sm" onClick={forget} disabled={busy} style={{ alignSelf: 'flex-start' }}>Forget Skybox login</button>
      )}
    </div>
  );
}

export default SkyboxSessionPanel;
